import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Calendar, Clock, ChevronDown } from 'lucide-react';
import SEOHead from '../../components/SEOHead';
import BlogNotFound from '../../components/BlogNotFound';
import { getBlogBySlug } from '../../data/blogs';

const Blog07052026 = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const blog = getBlogBySlug('your-manufacturing-plant-ready-boost-efficiency');

  useEffect(() => {
    setIsDesktop(window.innerWidth >= 1100);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);

      const sections = document.querySelectorAll('h2[id]');
      let current = '';

      sections.forEach(section => {
        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop <= 150) {
          current = section.getAttribute('id') || '';
        }
      });

      setActiveSection(current);
    };

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1100);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setTocOpen(false);
    }
  };

  const tableOfContents = [
    {
        "id": "context",
        "title": "1. Context",
        "originalTitle": "Context"
    },
    {
        "id": "problem-statement",
        "title": "2. Problem Statement",
        "originalTitle": "Problem Statement"
    },
    {
        "id": "core-framework-ai-in-manufacturing",
        "title": "3. Core Framework: AI in Manufacturing",
        "originalTitle": "Core Framework: AI in Manufacturing"
    },
    {
        "id": "comparative-analysis",
        "title": "4. Comparative Analysis",
        "originalTitle": "Comparative Analysis"
    },
    {
        "id": "business-use-cases",
        "title": "5. Business Use Cases",
        "originalTitle": "Business Use Cases"
    },
    {
        "id": "benefits-outcomes",
        "title": "6. Benefits & Outcomes",
        "originalTitle": "Benefits & Outcomes"
    },
    {
        "id": "challenges-realities",
        "title": "7. Challenges & Realities",
        "originalTitle": "Challenges & Realities"
    },
    {
        "id": "future-outlook",
        "title": "8. Future Outlook",
        "originalTitle": "Future Outlook"
    },
    {
        "id": "conclusion",
        "title": "9. Conclusion",
        "originalTitle": "Conclusion"
    },
    {
        "id": "call-to-action",
        "title": "10. Call to Action",
        "originalTitle": "Call to Action"
    }
];

  const faqData = [
    {
        "question": "What is this technology and how does it work?",
        "answer": "This technology represents a significant advancement in the field, offering innovative solutions to common challenges through modern approaches and proven methodologies."
    },
    {
        "question": "Who can benefit from implementing this solution?",
        "answer": "Organizations of all sizes can benefit, particularly those looking to improve efficiency, reduce costs, and enhance their competitive advantage through technological innovation."
    },
    {
        "question": "What are the main challenges in implementation?",
        "answer": "Key challenges include initial setup complexity, integration with existing systems, and ensuring proper training. However, with proper planning and support, these can be effectively managed."
    },
    {
        "question": "What ROI can be expected?",
        "answer": "While results vary by organization, typical implementations show significant improvements in operational efficiency, cost reduction, and enhanced capabilities within the first year."
    }
];

  const tldrPoints = [
    "The landscape of modern manufacturing is undergoing a profound transformation, largely driven by the integration of Artificial Intelligence (AI). This...",
    "Traditional manufacturing often grapples with a myriad of operational inefficiencies that directly impact profitability and competitiveness. These cha...",
    "The application of AI in manufacturing provides a robust framework for overcoming these entrenched challenges, leveraging data to drive intelligent au..."
];

  const keyTakeaways = [
    "Fast implementation strategies deliver measurable ROI within weeks, not months",
    "Agile methodologies reduce time-to-production by 60-80% compared to traditional approaches",
    "Cloud-native architecture enables rapid scaling without infrastructure bottlenecks",
    "Automated workflows eliminate manual bottlenecks and accelerate delivery timelines",
    "Real-time analytics provide immediate insights for faster decision-making"
];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": blog?.title,
        "description": blog?.excerpt,
        "image": blog?.featuredImage,
        "datePublished": blog?.publishDate,
        "dateModified": blog?.publishDate,
        "author": {
          "@type": "Organization",
          "name": "Agility"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Agility",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.agilitytech.ai/agility hex fonts.png"
          }
        },
        "keywords": blog?.seoKeywords.join(', '),
        "articleBody": "Is Your Manufacturing Plant Ready to Boost Efficiency by 30% with AI?",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://www.agilitytech.ai/insights/${blog?.slug}`
        },
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": [".tldr-section", ".key-takeaways-section"]
        }
      },
      {
        "@type": "HowTo",
        "name": blog?.title,
        "description": blog?.excerpt,
        "step": keyTakeaways.map((takeaway, index) => ({
          "@type": "HowToStep",
          "position": index + 1,
          "name": `Step ${index + 1}`,
          "text": takeaway
        }))
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      }
    ]
  };

  if (!blog) return <BlogNotFound />;

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <SEOHead
        title={`${blog?.title} | Agility Blog`}
        description={blog?.excerpt}
        keywords={blog?.seoKeywords.join(', ')}
        url={`https://www.agilitytech.ai/insights/${blog?.slug}`}
        image={blog?.featuredImage}
        type="article"
        article={true}
        author={blog?.author}
        publishedTime={new Date(blog?.publishDate || '').toISOString()}
        modifiedTime={new Date(blog?.publishDate || '').toISOString()}
        structuredData={structuredData}
      />

      {/* Blog Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'linear-gradient(to bottom, #0B0B0B, #050505)' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '1200px' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div className="mb-6">
              <span style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: 'rgba(20, 184, 166, 0.15)',
                color: '#14b8a6',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: '500',
                border: '1px solid rgba(20, 184, 166, 0.3)'
              }}>
                {blog.category}
              </span>
            </div>

            <h1 style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontFamily: 'Sora, sans-serif',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '1.5rem',
              lineHeight: '1.2',
              letterSpacing: '-0.02em'
            }}>
              {blog.title}
            </h1>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', color: '#9ca3af', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={16} />
                <span style={{ fontSize: '0.875rem' }}>{new Date(blog.publishDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={16} />
                <span style={{ fontSize: '0.875rem' }}>{blog.publishDate}</span>
              </div>
            </div>

            <p style={{
              fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
              color: '#d1d5db',
              lineHeight: '1.75',
              marginBottom: '2rem'
            }}>
              {blog.excerpt}
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {blog.tags.map((tag, index) => (
                <span key={index} style={{
                  padding: '0.25rem 0.75rem',
                  background: '#121212',
                  color: '#14b8a6',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  border: '1px solid #262626'
                }}>
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section style={{ background: '#050505', paddingBottom: '5rem' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', display: 'flex', minHeight: '100vh', gap: '3rem', flexDirection: isDesktop ? 'row' : 'column' }}>
          {/* Sidebar TOC - Desktop */}
          {isDesktop && (
            <div style={{
              position: 'sticky',
              top: '100px',
              height: 'fit-content',
              width: '260px',
              flexShrink: 0,
              alignSelf: 'flex-start',
              zIndex: 999
            }}>
              <h3 style={{
                color: '#ffffff',
                fontWeight: '600',
                marginBottom: '1rem',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>Table of Contents</h3>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {tableOfContents.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      padding: '0.5rem 1rem',
                      borderRadius: '0.5rem',
                      transition: 'all 0.2s',
                      borderLeft: activeSection === item.id ? '2px solid #14b8a6' : '2px solid transparent',
                      background: activeSection === item.id ? 'rgba(20,184,166,0.15)' : 'transparent',
                      color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                      cursor: 'pointer',
                      border: 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#d1d5db';
                        e.currentTarget.style.background = '#121212';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.color = '#9ca3af';
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    {item.title}
                  </button>
                ))}
              </nav>
            </div>
          )}

          {/* Mobile TOC */}
          {!isDesktop && (
            <div style={{
              width: '100%',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: '#121212',
                borderRadius: '12px',
                border: '1px solid #262626',
                padding: '1rem'
              }}>
                <button
                  onClick={() => setTocOpen(!tocOpen)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  <span>Table of Contents</span>
                  <ChevronDown style={{
                    transform: tocOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }} />
                </button>
                {tocOpen && (
                  <nav style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.5rem 1rem',
                          background: 'none',
                          border: 'none',
                          color: activeSection === item.id ? '#14b8a6' : '#9ca3af',
                          fontSize: '0.875rem',
                          cursor: 'pointer'
                        }}
                      >
                        {item.title}
                      </button>
                    ))}
                  </nav>
                )}
              </div>
            </div>
          )}

          {/* Article Content */}
          <div style={{ flex: 1 }}>
            <article style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>

              {/* TL;DR Section - AEO Optimized */}
              <div className="tldr-section" style={{
                background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1), rgba(20, 184, 166, 0.05))',
                borderLeft: '4px solid #14b8a6',
                borderRadius: '12px',
                padding: '2rem',
                marginBottom: '3rem',
                border: '1px solid rgba(20, 184, 166, 0.2)'
              }}>
                <h2 style={{
                  color: '#14b8a6',
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    background: '#14b8a6',
                    color: '#000000',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '700'
                  }}>TL;DR</span>
                  Quick Summary
                </h2>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}>
                  {tldrPoints.map((point, idx) => (
                    <li key={idx} style={{
                      color: '#e5e7eb',
                      fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                      lineHeight: '1.7',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <span style={{
                        color: '#14b8a6',
                        fontWeight: '700',
                        fontSize: '1.25rem',
                        lineHeight: '1'
                      }}>•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              
              <h2 id="context" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Context
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                The landscape of modern manufacturing is undergoing a profound transformation, largely driven by the integration of Artificial Intelligence (AI). This isn't just a buzzword; it's a critical technological trend that is redefining how goods are produced, enabling smarter decision-making, proactive maintenance, and unprecedented levels of automation. AI's rise to prominence in manufacturing now is a direct response to increasing global competition, demands for personalized products, and the need for more sustainable and resilient supply chains. Manufacturers are realizing that to stay competitive and drive innovation, embracing AI is no longer optional, but essential.
              </p>

              <h2 id="problem-statement" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Problem Statement
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Traditional manufacturing often grapples with a myriad of operational inefficiencies that directly impact profitability and competitiveness. These challenges include frequent and unpredictable equipment breakdowns leading to costly downtime, manual quality control processes that are prone to human error and inconsistency, suboptimal resource allocation, and a lack of real-time visibility into production performance. These inefficiencies translate into higher operating costs, increased waste, slower time-to-market, and a diminished capacity to adapt to changing market demands, ultimately hindering growth and innovation.
              </p>

              <h2 id="core-framework-ai-in-manufacturing" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Core Framework: AI in Manufacturing
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                The application of AI in manufacturing provides a robust framework for overcoming these entrenched challenges, leveraging data to drive intelligent automation and optimization.
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                AI in manufacturing refers to the deployment of artificial intelligence technologies, including machine learning, computer vision, natural language processing, and advanced analytics, to enhance various aspects of the production process. This encompasses everything from design and supply chain management to production, quality control, and predictive maintenance.
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                AI systems ingest vast amounts of data from various sources – IIoT sensors, production lines, ERP systems, quality inspection cameras, and more. Machine learning algorithms then analyze this data to identify patterns, predict outcomes, and automate decision-making. For instance, in predictive maintenance, AI models learn the normal operating parameters of machinery and can detect subtle anomalies indicating potential failure, triggering alerts before a breakdown occurs. In quality control, computer vision systems can rapidly inspect products for defects with a much higher accuracy and speed than human inspectors.
              </p>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                While powerful, AI in manufacturing is not without its limitations. The initial investment in hardware, software, and skilled personnel can be substantial. Data quality and availability are critical; "garbage in, garbage out" applies, meaning poor or insufficient data can lead to inaccurate predictions and decisions. Integrating AI systems with legacy manufacturing infrastructure can also be complex and time-consuming. Additionally, ethical considerations, data privacy, and the need for human oversight in critical decision-making processes remain important factors.
              </p>

              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Core Framework: AI in Manufacturing
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03))'
                }}>
                  <img
                    src="https://images.pexels.com/photos/265087/pexels-photo-265087.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080"
                    alt="Core Framework: AI in Manufacturing"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=90&auto=format';
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block'
                    }}
                  />
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Visual representation of core framework: ai in manufacturing concepts and implementation strategies.
                </p>
              </div>

              <h2 id="comparative-analysis" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Comparative Analysis
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                To better understand the distinct advantages, let's compare traditional manufacturing approaches with those enhanced by AI.
              </p>

              <div style={{
                overflowX: 'auto',
                margin: '2rem 0',
                border: '1px solid #262626',
                borderRadius: '8px'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'clamp(0.875rem, 1.8vw, 1rem)',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  <thead>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <th style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#14b8a6',
                        borderBottom: '2px solid #14b8a6'
                      }}>
                        Feature / Process
                      </th>
                      <th style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#14b8a6',
                        borderBottom: '2px solid #14b8a6'
                      }}>
                        Traditional Manufacturing
                      </th>
                      <th style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        textAlign: 'left',
                        fontWeight: '600',
                        color: '#14b8a6',
                        borderBottom: '2px solid #14b8a6'
                      }}>
                        AI-Enhanced Manufacturing
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Maintenance
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Reactive (break-fix) or Time-based (scheduled)
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Predictive, Prescriptive (AI predicts failures, suggests actions)
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Quality Control
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Manual inspections, sampling, post-production checks
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Automated, in-line, real-time defect detection with computer vision
                      </td>
                    </tr>
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Production Planning
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Manual scheduling, historical data, spreadsheet-based
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Dynamic, optimized by AI algorithms based on real-time demand &amp; capacity
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Efficiency
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Limited by human capacity, fixed processes
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Optimized continuously, potential for 20-30% improvement in OEE
                      </td>
                    </tr>
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Downtime
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        High, unpredictable
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Significantly reduced, proactive issue resolution
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Cost of Quality
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        High due to scrap, rework, warranty claims
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Reduced due to early defect detection, process optimization
                      </td>
                    </tr>
                    <tr style={{ background: '#121212' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Resource Utilization
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Often suboptimal, human-dependent
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Optimized by AI for energy, materials, and labor
                      </td>
                    </tr>
                    <tr style={{ background: '#1a1a1a' }}>
                      
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Adaptability
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Slow to adapt to changes
                      </td>
                      <td style={{
                        padding: 'clamp(0.75rem, 2vw, 1rem)',
                        color: '#e5e7eb',
                        borderBottom: '1px solid #262626'
                      }}>
                        Agile, responsive to market shifts and supply chain disruptions
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 id="business-use-cases" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Business Use Cases
              </h2>

              <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                
              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: High recall rates due to assembly defects, inefficient painting processes.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: AI-powered vision systems detect micro-defects on assembly lines, reducing recalls by up to 15%. Predictive analytics optimize paint shop operations, cutting material waste by 10%.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Pharmaceutical:
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: Strict quality control requirements, long drug development cycles, batch consistency issues.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: AI-driven sensor analytics ensure consistent batch quality, reducing deviations by 25%. Predictive modeling accelerates R&D by identifying promising compounds faster, shortening time-to-market by up to 6 months.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: Miniaturization demands precision, complex supply chains, rapid product obsolescence.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: AI optimizes intricate robotic assembly, improving accuracy by 30%. Demand forecasting powered by AI reduces inventory holding costs by 20% and prevents stockouts.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Heavy Industry/Machinery:
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Problem: Expensive machinery downtime, high maintenance costs, safety concerns.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Value: Predictive maintenance systems decrease unplanned downtime by 30-50%, extending equipment lifespan by 20% and reducing maintenance costs by 15-20%.
              </li>
              </ul>

              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Business Use Cases
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03))'
                }}>
                  <img
                    src="https://images.pexels.com/photos/2599244/pexels-photo-2599244.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080"
                    alt="Business Use Cases"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=90&auto=format';
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block'
                    }}
                  />
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Visual representation of business use cases concepts and implementation strategies.
                </p>
              </div>

              <h2 id="benefits-outcomes" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Benefits & Outcomes
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Embracing AI in manufacturing yields quantifiable improvements across both technical operations and broader business strategy.
              </p>

              <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                
              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Increased Overall Equipment Effectiveness (OEE): AI can boost OEE by 20-30% through optimized scheduling, reduced downtime, and improved quality.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Enhanced Predictive Capabilities: AI-driven predictive maintenance can reduce unplanned downtime by 30-50%, translating to millions in savings for large facilities.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Superior Quality Control: Computer vision systems achieve 99.9% accuracy in defect detection, significantly surpassing human capabilities and reducing scrap rates by 10-20%.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Optimized Energy Consumption: AI algorithms can reduce energy usage on production lines by 10-15% by identifying and minimizing inefficiencies.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Significant Cost Reduction: By minimizing waste, reducing downtime, and optimizing resource allocation, AI can lead to 15-25% operational cost savings.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Improved Competitiveness: Faster time-to-market, higher product quality, and greater production agility allow companies to respond quicker to market demands.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Enhanced Decision Making: Real-time data and AI-driven insights empower managers to make more informed, data-backed strategic and operational decisions.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Greater Workforce Productivity: Automating repetitive and hazardous tasks frees up human workers to focus on more complex problem-solving, innovation, and strategic initiatives.
              </li>

              <li style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '0.75rem',
                fontFamily: 'Inter, sans-serif'
              }}>
                Sustainable Operations: Reduced waste, optimized energy consumption, and more efficient resource use contribute to a smaller environmental footprint.
              </li>
              </ul>

              <h2 id="challenges-realities" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Challenges & Realities
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Implementing AI in manufacturing, while highly beneficial, is not without its hurdles. The complexity often lies in integrating disparate legacy systems, ensuring data quality and security, and developing or acquiring the specialized AI and data science skills needed to deploy and maintain these systems. There's also a significant upfront investment in technology and training, which can be a barrier for some organizations. Companies must prepare for a cultural shift, as AI changes workflows and roles, requiring a strategic approach to change management and employee upskilling.
              </p>

              <div style={{
                margin: '2.5rem 0',
                background: '#121212',
                borderRadius: '12px',
                padding: '1.5rem',
                border: '1px solid #262626'
              }}>
                <p style={{
                  color: '#14b8a6',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Challenges & Realities
                </p>
                <div style={{
                  width: '100%',
                  height: 'clamp(240px, 40vw, 320px)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  position: 'relative',
                  background: 'linear-gradient(135deg, rgba(20,184,166,0.08), rgba(0,150,136,0.03))'
                }}>
                  <img
                    src="https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080"
                    alt="Challenges & Realities"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=90&auto=format';
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block'
                    }}
                  />
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  marginTop: '1rem',
                  fontStyle: 'italic',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  Visual representation of challenges & realities concepts and implementation strategies.
                </p>
              </div>

              <h2 id="future-outlook" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Future Outlook
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Over the next 12 months, the trend of AI adoption in manufacturing is set to accelerate. We anticipate a greater emphasis on "edge AI," where AI processing happens closer to the data source (e.g., on machines), enabling real-time decision-making and reducing latency. The convergence of AI with other Industry 4.0 technologies like 5G, advanced robotics, and digital twins will become more seamless, creating highly intelligent and autonomous manufacturing environments. Furthermore, AI-powered generative design for product development and increasingly sophisticated AI for supply chain resilience will move from niche applications to mainstream adoption, driving innovation and efficiency across the entire value chain.
              </p>

              <h2 id="conclusion" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Conclusion
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                AI in modern manufacturing is more than just a technological upgrade; it's a strategic imperative that empowers businesses to transcend traditional limitations. By optimizing production, ensuring predictive maintenance, automating quality checks, and improving efficiency, AI unlocks significant operational and financial benefits. While challenges exist, the evidence clearly demonstrates that companies embracing AI are better positioned for sustained growth, innovation, and a leading edge in an increasingly competitive global market.
              </p>

              <h2 id="call-to-action" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Call to Action
              </h2>

              <p style={{
                color: '#e5e7eb',
                fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                lineHeight: '1.75',
                marginBottom: '1.5rem',
                textAlign: 'justify',
                fontFamily: 'Inter, sans-serif'
              }}>
                Ready to explore how AI can revolutionize your manufacturing operations? Connect with IndustryX.ai for a complimentary consultation or to discuss a proof-of-concept (POC) tailored to your specific challenges and goals. Let's build the future of manufacturing together.
              </p>


              {/* Key Takeaways Section - AEO Optimized */}
              <div className="key-takeaways-section" style={{
                background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.1), rgba(20, 184, 166, 0.05))',
                borderLeft: '4px solid #14b8a6',
                borderRadius: '12px',
                padding: '2rem',
                marginTop: '3rem',
                marginBottom: '3rem',
                border: '1px solid rgba(20, 184, 166, 0.2)'
              }}>
                <h2 style={{
                  color: '#14b8a6',
                  fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: '700',
                  marginBottom: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    background: '#14b8a6',
                    color: '#000000',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '700'
                  }}>⚡</span>
                  Key Takeaways - Fast Implementation Insights
                </h2>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem'
                }}>
                  {keyTakeaways.map((takeaway, idx) => (
                    <li key={idx} style={{
                      color: '#ffffff',
                      fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                      lineHeight: '1.7',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      background: 'rgba(255, 255, 255, 0.03)',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(20, 184, 166, 0.1)'
                    }}>
                      <span style={{
                        color: '#14b8a6',
                        fontWeight: '700',
                        fontSize: '1.125rem',
                        minWidth: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(20, 184, 166, 0.2)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        flexShrink: 0
                      }}>{idx + 1}</span>
                      <span>{takeaway}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* FAQ Section */}
              <h2 id="faq" style={{
                color: '#14b8a6',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontFamily: 'Sora, sans-serif',
                fontWeight: '600',
                marginBottom: '1.5rem',
                marginTop: '2rem',
                scrollMarginTop: '120px'
              }}>
                Frequently Asked Questions
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '3rem' }}>
                {faqData.map((faq, idx) => (
                  <div key={idx} style={{
                    background: '#121212',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid #262626',
                    transition: 'border-color 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#14b8a6'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#262626'}>
                    <h3 style={{
                      color: '#ffffff',
                      fontSize: 'clamp(1.125rem, 2.5vw, 1.25rem)',
                      fontWeight: '600',
                      marginBottom: '0.75rem',
                      fontFamily: 'Sora, sans-serif',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem'
                    }}>
                      <span style={{ color: '#14b8a6', flexShrink: 0 }}>Q{idx + 1}.</span>
                      <span>{faq.question}</span>
                    </h3>
                    <p style={{
                      color: '#e5e7eb',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)',
                      lineHeight: '1.75',
                      marginLeft: '2rem',
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>

              {/* CTA Section */}
              <div style={{
                marginTop: '3rem',
                background: '#121212',
                borderRadius: '16px',
                padding: 'clamp(1.5rem, 4vw, 2rem)',
                border: '1px solid #262626',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '384px',
                  height: '384px',
                  background: 'rgba(20, 184, 166, 0.1)',
                  borderRadius: '50%',
                  filter: 'blur(80px)'
                }}></div>
                <div style={{ position: 'relative', zIndex: 10, textAlign: 'center' }}>
                  <h3 style={{
                    fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
                    fontWeight: '700',
                    color: '#ffffff',
                    marginBottom: '1rem',
                    fontFamily: 'Sora, sans-serif'
                  }}>
                    Ready to Transform Your Business?
                  </h3>
                  <p style={{
                    color: '#e5e7eb',
                    fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                    marginBottom: '1.5rem',
                    maxWidth: '600px',
                    margin: '0 auto 1.5rem',
                    fontFamily: 'Inter, sans-serif'
                  }}>
                    Contact us today for a personalized consultation and discover how we can help you achieve your goals.
                  </p>
                  <Link
                    to="/contact"
                    style={{
                      display: 'inline-block',
                      padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.5rem)',
                      border: '2px solid #14b8a6',
                      color: '#14b8a6',
                      borderRadius: '8px',
                      fontWeight: '600',
                      textDecoration: 'none',
                      transition: 'all 0.3s',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 'clamp(0.875rem, 2vw, 1rem)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#14b8a6';
                      e.currentTarget.style.color = '#ffffff';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#14b8a6';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Get Started Today
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          style={{
            position: 'fixed',
            bottom: 'clamp(1.5rem, 4vw, 2rem)',
            right: 'clamp(1.5rem, 4vw, 2rem)',
            width: 'clamp(44px, 10vw, 48px)',
            height: 'clamp(44px, 10vw, 48px)',
            background: '#121212',
            border: '2px solid #262626',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#14b8a6',
            cursor: 'pointer',
            transition: 'all 0.3s',
            zIndex: 50
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#14b8a6';
            e.currentTarget.style.boxShadow = '0 0 20px rgba(20, 184, 166, 0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#262626';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <ArrowUp size={20} />
        </button>
      )}
    </div>
  );
};

export default Blog07052026;
